import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  User,
  MapPin,
  Star,
  Gift,
  Heart,
  Package,
  RotateCcw,
  Ticket,
  BadgeCheck,
  Users,
  HelpCircle,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const NAV = [
  { to: "profile", label: "Profile", icon: User },
  { to: "addresses", label: "Manage Address", icon: MapPin },
  { to: "reward-history", label: "Reward History", icon: Star },
  { to: "reward-zone", label: "Reward zone", icon: Gift },
  { to: "wishlist", label: "Wishlist", icon: Heart },
  { to: "orders", label: "My orders", icon: Package },
  { to: "returns", label: "Returns", icon: RotateCcw },
  { to: "coupons", label: "My Coupons", icon: Ticket },
  { to: "membership", label: "My Membership", icon: BadgeCheck },
  { to: "refer", label: "Refer and earn", icon: Users },
  { to: "help", label: "Help", icon: HelpCircle },
];

export default function AccountLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    setLogoutOpen(false);
    try {
      await logout();
      toast.success("Signed out. See you soon!");
      nav("/", { replace: true });
    } catch {
      toast.error("Could not log out. Please try again.");
    }
  };

  const initials = (user?.name || "You")
    .split(" ")
    .map((w) => w.trim()[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <main className="bg-[#F5F3FB] min-h-[calc(100vh-160px)]">
      <div className="dyntra-container py-6 sm:py-10">
        {/* Mobile toggle */}
        <button
          type="button"
          data-testid="account-mobile-menu"
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 mb-3"
        >
          {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          Account menu
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
          {/* Sidebar */}
          <aside
            className={`${mobileOpen ? "block" : "hidden"} lg:block rounded-2xl border border-gray-100 bg-white p-3 h-fit sticky top-24`}
            data-testid="account-sidebar"
          >
            <div className="flex items-center gap-3 px-3 py-3 border-b border-gray-100 mb-2">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#4B2AAD] to-[#6B4CD9] text-white flex items-center justify-center font-bold text-sm">
                {initials}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-gray-900 truncate">
                  {user?.name || "Welcome"}
                </p>
                <p className="text-[11px] text-gray-500 truncate">
                  {user?.email || "Sign in for full access"}
                </p>
              </div>
            </div>

            <nav className="flex flex-col gap-0.5">
              {NAV.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`account-nav-${to}`}
                  className={({ isActive }) =>
                    [
                      "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors",
                      isActive
                        ? "bg-[#4B2AAD] text-white shadow-[0_6px_18px_rgba(75,42,173,0.25)]"
                        : "text-gray-700 hover:bg-[#F3EFFC] hover:text-[#4B2AAD]",
                    ].join(" ")
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{label}</span>
                </NavLink>
              ))}
              <button
                type="button"
                data-testid="account-nav-logout"
                onClick={() => setLogoutOpen(true)}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
              >
                <LogOut className="h-4 w-4 shrink-0" />
                <span>Logout</span>
              </button>
            </nav>
          </aside>

          {/* Content */}
          <section
            data-testid="account-content"
            className="min-w-0"
            key={loc.pathname}
          >
            <Outlet />
          </section>
        </div>
      </div>

      <Dialog open={logoutOpen} onOpenChange={setLogoutOpen}>
        <DialogContent className="sm:max-w-sm rounded-2xl">
          <DialogHeader className="items-center text-center">
            <div className="h-12 w-12 rounded-2xl bg-[#F3EFFC] flex items-center justify-center mx-auto">
              <LogOut className="h-5 w-5 text-[#4B2AAD]" />
            </div>
            <DialogTitle className="mt-3 font-display">Logout</DialogTitle>
            <DialogDescription>
              Are you sure you want to log out of your Dyntra account?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center gap-2">
            <button
              type="button"
              onClick={() => setLogoutOpen(false)}
              className="dyntra-btn-ghost flex-1"
              data-testid="account-logout-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="dyntra-btn-primary flex-1"
              data-testid="account-logout-confirm"
            >
              Logout
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
